import React from 'react';

import { format, parse } from 'date-fns';
import { defaultTo } from 'lodash-es';
import { useIntl } from 'react-intl';

import useMediaQuery from 'hooks/use-media-query';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMenuContext } from 'state/menu';
import {
  getAddressMessage,
  getOrderingDisabledStoreSelectionMessage,
  isCatering
} from 'utils/service-mode';
import { messages } from 'utils/service-mode/service-mode-messages';

import ChangeServiceModeLink from '../change-service-mode-link';
import { AdditionalTextContainer } from '../styled';
import { IServiceModeAddressProps } from '../types';

const ServiceModeAddress: React.FC<IServiceModeAddressProps> = ({
  store,
  serviceMode,
  userAddress,
  cateringPickupDateTime
}) => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery('mobile');
  const enableOrdering = defaultTo(useFlag(LaunchDarklyFlag.ENABLE_ORDERING), true);
  const showUnavailableRestaurant = useFlag(
    LaunchDarklyFlag.DISPLAY_SELECTED_RESTAURANT_INFORMATION_WHEN_RESTAURANT_UNAVAILABLE
  );
  const { showStaticMenu } = useMenuContext();
  const addressMessage = getAddressMessage(
    store,
    serviceMode,
    userAddress,
    formatMessage,
    isMobile
  );

  const orderingDisabledStoreSelectionMessage = getOrderingDisabledStoreSelectionMessage(
    store,
    formatMessage
  );

  // show time for catering pickup
  const additionalText =
    isCatering(serviceMode) &&
    cateringPickupDateTime &&
    format(parse(cateringPickupDateTime, 'yyyy-MM-dd h:mm aaaa', new Date()), 'MM/dd/yy h:mm aaaa');

  let primaryText = orderingDisabledStoreSelectionMessage;

  if (enableOrdering) {
    primaryText = showStaticMenu ? formatMessage(messages.forItemAvailability) : addressMessage;
  }

  // NOTE: This is a hack. To be removed when store selection 2.0 is fully integrated
  // BK UK wants to show the store information when an unavailable store is "selected"
  const selectedUnavailableStoreNumber =
    'selectedUnavailableStoreNumber' in store && store.selectedUnavailableStoreNumber;
  if (showUnavailableRestaurant && selectedUnavailableStoreNumber) {
    primaryText = store?.physicalAddress?.address1 || primaryText;
  }

  return (
    <div data-testid="service-mode-address">
      <h3>{primaryText}</h3>
      {additionalText && <AdditionalTextContainer>{additionalText}</AdditionalTextContainer>}
      <ChangeServiceModeLink
        storeSelected={
          store.hasSelection || (showUnavailableRestaurant && !!selectedUnavailableStoreNumber)
        }
        orderingEnabled={enableOrdering}
      />
    </div>
  );
};

export default ServiceModeAddress;
